<template>
  <div v-if="filteredList" id="content-page-table" class="table-fix-head" :style="fixHeadCalculatedStyle">
    <table v-if="filteredList.length > 0" class="table table-hover table-sm table-fit align-middle">
      <thead>
        <tr>
          <th v-if="showItemField"></th>
          <th v-if="selectedSiteId === 0">{{ $t('global.site') }}</th>
          <th v-if="showItemField">
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th>{{ $t('field.generic.quantity') }}</th>
          <th class="d-none d-sm-table-cell" v-if="!ignoreSignature">{{ $t('field.masterdata.lot') }}</th>
          <th class="d-none d-sm-table-cell" v-if="!ignoreSignature && hasBestBeforeOnAnyLine">{{ $t('field.masterdata.bestBefore') }}</th>
          <th class="d-none d-sm-table-cell" v-if="!ignoreSignature && hasExpiryOnAnyLine">{{ $t('field.masterdata.expiry') }}</th>
          <th class="d-table-cell d-sm-none" v-if="!ignoreSignature">{{ $t('global.propertiesShort') }}</th>
          <th v-if="!ignoreSignature && userSettingsStore.data.showBarcode"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="stock in filteredList" :key="stock.id">
          <td v-if="showItemField">
            <div class="btn-group">
              <RouterLink :to="{ name: 'item', params: { itemId: stock.item.id }}" :title="$t('menu.items')" class="btn btn-outline-secondary btn-sm">
                <font-awesome-icon :icon="['fas', 'rectangle-list']" />
              </RouterLink>
              <RouterLink :to="{ name: 'stock-change', params: { itemId: stock.item.id } }" :title="$t('menu.stock-changes')" class="btn btn-outline-secondary btn-sm">
                <font-awesome-icon :icon="['fas', 'plus-minus']" />
              </RouterLink>
            </div>
          </td>
          <td v-if="selectedSiteId === 0"><sup class="text-secondary">{{ stock.site.code }}&nbsp;</sup>{{
            stock.site.name
          }}
          </td>
          <td v-if="showItemField">
            <ItemField :item="stock.item" />
          </td>
          <td class="text-end">
            <QuantityField :quantity="stock.modifiedQuantity ?? stock.quantity" :estimated-quantity="stock.estimatedQuantity"
              :unit-name="stock.item.baseUnitCode" :pad-length="maxQuantityLength"
              :show-change-controls="useEditMode" :open-input="useEditMode" :min-quantity=0
              v-on:update:quantity="updateQuantity(stock, $event)"/>
          </td>
          <td class="d-none d-sm-table-cell" v-if="!ignoreSignature">
            <LotField :item="stock.item" :lot="stock.lot" />
          </td>
          <td class="d-none d-sm-table-cell text-center" v-if="!ignoreSignature && hasBestBeforeOnAnyLine">
            <BestBeforeField :stock="stock" />
          </td>
          <td class="d-none d-sm-table-cell text-center" v-if="!ignoreSignature && hasExpiryOnAnyLine">
            <ExpirationField :stock="stock" />
          </td>
          <td class="d-table-cell d-sm-none" v-if="!ignoreSignature">
            <div v-if="stock.lot">
              <LotField :item="stock.item" :lot="stock.lot" />
            </div>
            <div v-if="hasBestBeforeOnAnyLine">
              <BestBeforeField :stock="stock" />
            </div>
            <div v-if="hasExpiryOnAnyLine">
              <ExpirationField :stock="stock" />
            </div>
          </td>
          <td v-if="!ignoreSignature && userSettingsStore.data.showBarcode" class="bg-white p-1">
            <TradeItemBarcode v-if="stock.item.requiresLabel" :itemId="stock.item.id" :itemSignature="stock"
              :size="'3rem'" @click="toggleSignatureItemSelection(stock)" :class="{ 'bg-warning': isStored(stock) }" />
          </td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
  <OpenOrdersForItemModal ref="openOrdersModal" />
</template>

<script setup lang="ts">
import { BestBeforeField, ExpirationField, LotField, ItemField, QuantityField } from "@/components/fields";
import Stock from "@/models/frontend/Stock";
import { PropType, computed, onMounted, onUnmounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import TradeItemBarcode from "@/components/diagram/TradeItemBarcode.vue";
import OpenOrdersForItemModal from "@/components/orders/OpenOrdersForItemModal.vue";
import { useItemLabelsStore, useUserSettingsStore, useSessionStore } from "@/stores";
import IItemWithSignatureRaw from "@/models/IItemWithSignatureRaw.ts";

const props = defineProps({
  filteredList: { type: Array as PropType<Array<Stock>>, required: true },
  useEditMode: { type: Boolean, default: false },
  showItemField: { type: Boolean, default: true },
  ignoreSignature: { type: Boolean, default: false },
});

const itemLabelsStore = useItemLabelsStore();
const sessionStore = useSessionStore();
const { selectedSiteId } = storeToRefs(sessionStore);
const userSettingsStore = useUserSettingsStore();

const offsetTop = ref(0);
const offsetTopInterval = ref<NodeJS.Timeout | null>(null);

onMounted(() => {
  offsetTopInterval.value = setInterval(() => {
    offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
  }, 1000);
  offsetTop.value = document.querySelector('#content-page-table')?.getBoundingClientRect().top ?? 0;
});

onUnmounted(() => {
  if (offsetTopInterval.value) {
    clearInterval(offsetTopInterval.value);
    offsetTopInterval.value = null;
  }
});

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});

const hasBestBeforeOnAnyLine = computed(() => {
  return props.filteredList !== undefined && props.filteredList.findIndex((x) => !!x.bestBefore) !== -1;
});

const hasExpiryOnAnyLine = computed(() => {
  return props.filteredList !== undefined && props.filteredList.findIndex((x) => !!x.expiry) !== -1;
});

const maxQuantityLength = computed(() => {
  return props.filteredList.reduce((acc, item) => Math.max(acc, item.quantity.toString().length), 0);
});

const updateQuantity = (item: Stock, quantity: number) => {
  item.modifiedQuantity = quantity;
};

const isStored = (stock: IItemWithSignatureRaw): boolean => {
  return itemLabelsStore.isStored(stock);
}

const toggleSignatureItemSelection = (item: IItemWithSignatureRaw) => {
  if (itemLabelsStore.isStored(item)) {
    itemLabelsStore.removeSignatureItem(item);
  } else {
    itemLabelsStore.addSignatureItem(item);
  }
};
</script>
